<template>
  <div v-if="bracketPrices.length" class="space-y-1 text-xs">
    <CText v-if="showHeader" class="text-gray-800" variant="system-xs">
      {{ t('price.bracket.bracket_pricing') }}
    </CText>
    <div class="space-y-1">
      <div
        v-for="({ minimumQuantity, price, unit }, index) in bracketPrices"
        :key="index"
        class="flex items-center"
        :class="{ 'justify-between': showCompletedIcon }"
      >
        <div>
          <CIcon
            v-if="checkMarks[index]"
            name="check"
            class="text-green-500 align-text-bottom"
            data-test="bracket-price-check"
          />
          <CText
            class="text-gray-800"
            :class="{ 'ml-5': showCompletedIcon && !checkMarks[index] }"
            el="span"
            variant="system-xs"
          >
            {{ t('price.bracket.price_from_unit', { num: minimumQuantity, unit }) }}:
          </CText>
        </div>
        <PriceIndicator
          class="inline-block ml-2 text-gray-800"
          :class="{ 'ml-2': !showCompletedIcon }"
          :price="price.toString()"
          :sales-units="salesUnits"
          :price-unit="unit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { BracketPrice } from '@/plugins/services/price/types';
import { WebSalesUnit } from '@/types/products';

type Props = {
  showHeader?: boolean;
  showCompletedIcon?: boolean;
  netPrice?: string;
  salesUnits?: WebSalesUnit;
  articleNumber: string;
};

const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
  showCompletedIcon: false,
  netPrice: '',
  salesUnits: () => ({}),
});

const { t } = useI18n();
const { $priceService } = useNuxtApp();

inject('unit', t('piece'));

const bracketPrices = ref<BracketPrice[]>([]);

const checkMarks = computed(() => {
  return bracketPrices.value.map(({ price }) => props.netPrice === price);
});

watch(
  () => props.articleNumber,
  async () => {
    const { data: brackets, error } = await $priceService.fetchBrackets(props.articleNumber);
    bracketPrices.value = brackets || [];
  },
  { immediate: true },
);
</script>

<script lang="ts">
export default defineComponent({
  name: 'BracketPricing',
});
</script>
